import { firstNonBlank, notEmpty } from "@de-xima/xima-common-js-lang";
import { Typr, type FontObject } from "@xenorange/typr.js";

const TyprFontMime = new Set([
  "font/ttf",
  "application/x-font-ttf",
  "font/otf",
  "font/opentype",
  "application/x-font-opentype",
  "font/woff",
  "application/font-woff",
]);

const FontMimes = new Set([...TyprFontMime, "font/woff2", "application/font-woff2"]);

const SampleFontFamily = "elfinder-quicklook-font";

const MaxFileSize = 1024 * 1024 * 20;

interface FontFaceHolder {
  fontFace: FontFace | undefined;
}

/**
 * Checks if the given font object has a glyph for all code point
 * of the given text.
 * @param fontObject Font object.
 * @param text Text with Unicode code points to check.
 * @returns True if the font has a glyph for all code points of the text.
 */
function hasGlyphForText(fontObject: FontObject, text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    const g = Typr.U.codeToGlyph(fontObject, text.charCodeAt(i));
    if (g === 0) {
      return false;
    }
  }
  return true;
}

/**
 * Looks for the first n Unicode code points for which the given font
 * has a glyph and returns a string containing these code points.
 * @param fontObject The font object.
 * @param n The number of code points to look for.
 * @returns A string containing the first n code points for which the font has a glyph.
 */
function findFirstNGlyphs(fontObject: FontObject, n: number): string {
  const text: string[] = [];
  for (let i = 0x21; i < 0xffff && text.length < n; i++) {
    const glyph = Typr.U.codeToGlyph(fontObject, i);
    if (glyph > 0) {
      const path = Typr.U.glyphToPath(fontObject, glyph);
      // e.g. material icons have dummy empty glyphs...
      if (path.cmds.length > 0) {
        text.push(String.fromCharCode(i));
      }
    }
  }
  return text.join("");
}

/**
 * Creates a DOM element representing a text item, such as the name of the file,
 * or the font family name.
 * @param label Optional label for the item.
 * @param text The text to display, e.g. the file name or font family name.
 * @param link Optional URL. When given, the text is displayed as a link.
 * @returns The DOM element representing the text item.
 */
function createFontInfoDataItem(label: string, text: string, link?: string) {
  const item = document.createElement("div");
  item.classList.add("elfinder-quicklook-info-font-item");
  if (notEmpty(label)) {
    const labelElement = document.createElement("span");
    labelElement.textContent = `${label}`;
    item.appendChild(labelElement);
    item.appendChild(document.createTextNode(": "));
  }
  const textElement = document.createElement("span");
  if (notEmpty(link)) {
    const linkElement = document.createElement("a");
    linkElement.textContent = text;
    linkElement.href = link;
    linkElement.rel = "noopener noreferrer";
    linkElement.target = "_blank";
    textElement.appendChild(linkElement);
  } else {
    textElement.textContent = text;
  }
  item.appendChild(textElement);
  return item;
}

/**
 * Creates a DOM element containing details about the font file.
 * @param fm The elFinder instance.
 * @param fontObject The font object.
 * @param file The font file.
 * @returns The DOM element containing the font details.
 */
function createFontInfoDetails(
  fm: elFinder.ElFinderInstance,
  fontObject: FontObject | undefined,
  file: elFinder.ElFinderFile,
): HTMLElement {
  const info = document.createElement("div");
  info.classList.add("elfinder-quicklook-info-font");

  info.appendChild(createFontInfoDataItem("", file.name));
  info.appendChild(createFontInfoDataItem("", fm.mime2kind(file)));
  info.appendChild(createFontInfoDataItem("", fm.formatSize(file.size ?? 0)));

  const name = fontObject?.name;
  if (name !== undefined) {
    if (notEmpty(name.fullName)) {
      info.appendChild(createFontInfoDataItem("", name.fullName));
    }
    if (notEmpty(name.fontFamily)) {
      const parts = [name.fontFamily];
      if (name.fontSubfamily !== undefined) {
        parts.push(name.fontSubfamily);
      }
      info.appendChild(createFontInfoDataItem(fm.i18n("fontFontFamily"), parts.join(" / ")));

      const moreInfoElements: HTMLElement[] = [];
      if (notEmpty(name.ID)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontId"), name.ID));
      }
      if (notEmpty(name.postScriptName)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontPostScriptName"), name.postScriptName));
      }
      if (notEmpty(name.version)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontVersion"), name.version));
      }
      if (notEmpty(name.description)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontDescription"), name.description));
      }
      if (notEmpty(name.designer)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontDesigner"), name.designer, name.urlDesigner));
      }
      if (notEmpty(name.manufacturer)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontManufacturer"), name.manufacturer, name.urlVendor));
      }
      if (notEmpty(name.copyright)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontCopyright"), name.copyright));
      }
      if (notEmpty(name.trademark)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontTrademark"), name.trademark));
      }
      if (notEmpty(name.licence)) {
        moreInfoElements.push(createFontInfoDataItem(fm.i18n("fontLicense"), name.licence, name.licenceURL));
      }
      if (moreInfoElements.length > 0) {
        const more = document.createElement("div");
        more.classList.add("elfinder-quicklook-info-font-item", "elfinder-quicklook-info-font-more");
        more.textContent = fm.i18n("fontShowMore");
        more.addEventListener("click", () => {
          more.remove();
          for (const element of moreInfoElements) {
            info.appendChild(element);
          }
        });
        info.appendChild(more);
      }
    }
  }

  return info;
}

/**
 * Finds a piece of sample text to display as a font preview. Ensures that
 * the text contains only code points for which the font has a glyph.
 * @param fontObject The font object.
 * @param fm The elFinder instance.
 * @returns The sample text.
 */
function resolvedSampleText(fontObject: FontObject | undefined, fm: elFinder.ElFinderInstance): string {
  const i18nSampleText = fm.i18n("fontSampleText");
  const sampleText = firstNonBlank(
    fontObject?.name?.sampleText,
    i18nSampleText === "fontSampleText" ? undefined : i18nSampleText,
    "A quick brown fox jumps over the lazy dog.",
  );
  return fontObject === undefined || hasGlyphForText(fontObject, sampleText)
    ? sampleText
    : findFirstNGlyphs(fontObject, 10);
}

function setQuickLookFontFace(fontFaceHolder: FontFaceHolder, fontFace: FontFace | undefined) {
  if (fontFaceHolder.fontFace !== undefined) {
    document.fonts.delete(fontFaceHolder.fontFace);
    fontFaceHolder.fontFace = undefined;
  }
  if (fontFace !== undefined) {
    document.fonts.add(fontFace);
    fontFaceHolder.fontFace = fontFace;
  }
}

function tryParseFont(fontData: ArrayBuffer, file: elFinder.ElFinderFile): FontObject | undefined {
  try {
    return TyprFontMime.has(file.mime) ? Typr.parse(fontData)[0] : undefined;
  } catch (e) {
    console.debug("Failed to parse font, no additional font details will be shown", e);
    return undefined;
  }
}

/**
 * Loads the font from the given file, creates a DOM element with details
 * about the font, and adds it to the preview area.
 * @param quickLook The quick look instance.
 * @param file The font file.
 * @param signal An abort signal. This asynchronous function will abort
 * if the signal is aborted.
 * @returns A promise that resolves when the font preview has been added.
 */
async function addFontPreview(
  quickLook: elFinder.ElFinderQuickLookCommandHandler,
  file: elFinder.ElFinderFile,
  fontFaceHolder: { fontFace: FontFace | undefined },
  signal: AbortSignal,
) {
  const fontData = await quickLook.fm.getContents(file.hash, "arraybuffer");
  if (signal.aborted) {
    return;
  }

  const fontFace = new FontFace(SampleFontFamily, fontData);
  await fontFace.load();
  if (signal.aborted) {
    return;
  }

  setQuickLookFontFace(fontFaceHolder, fontFace);

  const fontObject = tryParseFont(fontData, file);
  const sampleText = resolvedSampleText(fontObject, quickLook.fm);
  const fontDetails = createFontInfoDetails(quickLook.fm, fontObject, file);

  const fontTextPreview = document.createElement("div");
  fontTextPreview.classList.add("elfinder-quicklook-preview-font-sample");
  fontTextPreview.textContent = sampleText;
  fontTextPreview.style.fontFamily = SampleFontFamily;

  const icon = quickLook.preview.parent().find(".elfinder-cwd-icon").clone();

  const wrapper = document.createElement("div");
  wrapper.classList.add("elfinder-quicklook-preview-font");

  const infoWrapperBase = document.createElement("div");
  infoWrapperBase.classList.add("elfinder-quicklook-preview-font-wrapper");

  for (const child of icon.toArray()) {
    infoWrapperBase.appendChild(child);
  }
  infoWrapperBase.appendChild(fontDetails);

  wrapper.appendChild(infoWrapperBase);
  wrapper.appendChild(fontTextPreview);

  quickLook.hideinfo();
  quickLook.preview.append(wrapper);
}

/**
 * A quick look plugin that can show a preview of a font file (OTF, TTF, WOFF, WOFF2)
 * by rendering a sample text with the font and displaying it as an SVG.
 * @param quickLook The quick look command plugin.
 */
export const ElFinderFontQuickLookPlugin: elFinder.ElFinderQuickLookPlugin = class {
  constructor(quickLook: elFinder.ElFinderQuickLookCommandHandler) {
    let abortController: AbortController | undefined;

    const fontFaceHolder: { fontFace: FontFace | undefined } = { fontFace: undefined };

    quickLook.preview.on(quickLook.evUpdate, (e) => {
      if (FontMimes.has(e.file.mime) && e.file.size && e.file.size < MaxFileSize) {
        e.stopImmediatePropagation();
        abortController?.abort();
        abortController = new AbortController();
        addFontPreview(quickLook, e.file, fontFaceHolder, abortController.signal).catch((error) => {
          console.error("Failed to load font preview", error);
        });
      }
    });

    quickLook.preview.on("change", () => {
      setQuickLookFontFace(fontFaceHolder, undefined);
      abortController?.abort();
    });
  }
};
